// Complete solution for AccessControlContext.js with proper Firebase integration

import React, { createContext, useContext, useState, useEffect } from 'react';
import { collection, doc, onSnapshot, setDoc, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './AuthContext';

const AccessControlContext = createContext();

export const useAccessControl = () => {
  const context = useContext(AccessControlContext);
  if (!context) {
    throw new Error('useAccessControl must be used within AccessControlProvider');
  }
  return context;
};

export const FIELD_ACCESS_LEVELS = {
  HIDDEN: 'hidden',
  VIEW_ONLY: 'view_only', 
  EDIT: 'edit',
  FULL: 'full'
};

// Default permissions - these will be saved to Firebase on first load
const DEFAULT_PERMISSIONS = {

  SaveLoad: { viewer: 'view_only', editor: 'edit', admin: 'full', superadmin: 'full' },

  // Guide General Settings - Core Information
  isIntroductoryStop: { viewer: 'view_only', editor: 'edit', admin: 'full', superadmin: 'full' },
  ai_generated_personalization: { viewer: 'view_only', editor: 'edit', admin: 'full', superadmin: 'full' },
  thumbnailUrl: { viewer: 'view_only', editor: 'edit', admin: 'full', superadmin: 'full' },
  theme: { viewer: 'view_only', editor: 'edit', admin: 'full', superadmin: 'full' },
  itinerary_name: { viewer: 'view_only', editor: 'edit', admin: 'full', superadmin: 'full' },
  country: { viewer: 'view_only', editor: 'edit', admin: 'full', superadmin: 'full' },
  city: { viewer: 'view_only', editor: 'edit', admin: 'full', superadmin: 'full' },
  language: { viewer: 'view_only', editor: 'edit', admin: 'full', superadmin: 'full' },

  // Guide General Settings - Itinerary Details
  itinerary_duration: { viewer: 'view_only', editor: 'edit', admin: 'full', superadmin: 'full' },
  itinerary_difficulty: { viewer: 'view_only', editor: 'edit', admin: 'full', superadmin: 'full' },
  itinerary_rating: { viewer: 'view_only', editor: 'edit', admin: 'full', superadmin: 'full' },
  guide_type: { viewer: 'view_only', editor: 'edit', admin: 'full', superadmin: 'full' },
  itinerary_description: { viewer: 'view_only', editor: 'edit', admin: 'full', superadmin: 'full' },

  // Guide General Settings - Classification
  itinerary_categories: { viewer: 'view_only', editor: 'edit', admin: 'full', superadmin: 'full' },
  itinerary_tags: { viewer: 'view_only', editor: 'edit', admin: 'full', superadmin: 'full' },

  // Guide General Settings - File & Translation
  translated_itinerary: { viewer: 'view_only', editor: 'edit', admin: 'full', superadmin: 'full' },
  parent_guide_id: { viewer: 'view_only', editor: 'edit', admin: 'full', superadmin: 'full' },
  filename: { viewer: 'view_only', editor: 'edit', admin: 'full', superadmin: 'full' },

  // Guide General Settings - Location Coordinates
  itinerary_coordinates: { viewer: 'view_only', editor: 'edit', admin: 'full', superadmin: 'full' },

  // Core Identification
  name: { viewer: 'view_only', editor: 'edit', admin: 'full', superadmin: 'full' },
  subtitle: { viewer: 'view_only', editor: 'edit', admin: 'full', superadmin: 'full' },
  category: { viewer: 'view_only', editor: 'edit', admin: 'full', superadmin: 'full' },
  tier: { viewer: 'view_only', editor: 'edit', admin: 'full', superadmin: 'full' },
  
  // Media
  photoUrl: { viewer: 'view_only', editor: 'edit', admin: 'full', superadmin: 'full' },
  sphericalPhotoUrl: { viewer: 'view_only', editor: 'edit', admin: 'full', superadmin: 'full' },

  // Location
  coordinates: { viewer: 'view_only', editor: 'edit', admin: 'full', superadmin: 'full' },

  // Core Content
  inDepthDescription: { viewer: 'hidden', editor: 'view_only', admin: 'edit', superadmin: 'full' },
  description: { viewer: 'view_only', editor: 'edit', admin: 'full', superadmin: 'full' },
  
  // Audio Narration
  audioUrl: { viewer: 'hidden', editor: 'view_only', admin: 'edit', superadmin: 'full' },
  
  // Metadata
  estimatedDuration: { viewer: 'view_only', editor: 'edit', admin: 'full', superadmin: 'full' },
  openingHours: { viewer: 'view_only', editor: 'edit', admin: 'full', superadmin: 'full' },
  keywords: { viewer: 'view_only', editor: 'edit', admin: 'full', superadmin: 'full' },
  
  // Supplementary Content
  historicalContext: { viewer: 'hidden', editor: 'view_only', admin: 'edit', superadmin: 'full' },
  culturalSignificance: { viewer: 'hidden', editor: 'view_only', admin: 'edit', superadmin: 'full' },
  hiddenGems: { viewer: 'hidden', editor: 'hidden', admin: 'view_only', superadmin: 'full' },
  
  // Navigation
  nextStop: { viewer: 'view_only', editor: 'edit', admin: 'full', superadmin: 'full' },
  
  // System features
  ai_settings: { viewer: 'hidden', editor: 'view_only', admin: 'edit', superadmin: 'full' },
  userManagement: { viewer: 'hidden', editor: 'hidden', admin: 'edit', superadmin: 'full' },
  permissionConfig: { viewer: 'hidden', editor: 'hidden', admin: 'hidden', superadmin: 'full' },
  
  // Feature-based permissions
  translation: { viewer: 'hidden', editor: 'edit', admin: 'full', superadmin: 'full' },
  audioGeneration: { viewer: 'hidden', editor: 'edit', admin: 'full', superadmin: 'full' },
  tourPlanning: { viewer: 'hidden', editor: 'edit', admin: 'full', superadmin: 'full' },
  regeneratePointIds: { viewer: 'hidden', editor: 'edit', admin: 'full', superadmin: 'full' },
  addNewPoi: { viewer: 'hidden', editor: 'edit', admin: 'full', superadmin: 'full' },

  // Export features
  firebaseExport: { viewer: 'hidden', editor: 'edit', admin: 'full', superadmin: 'full' },
  sqlExport: { viewer: 'view_only', editor: 'edit', admin: 'full', superadmin: 'full' },
  urlGeneration: { viewer: 'view_only', editor: 'edit', admin: 'full', superadmin: 'full' },
  saveDraft: { viewer: 'hidden', editor: 'edit', admin: 'full', superadmin: 'full' },
  saveJSON: { viewer: 'hidden', editor: 'edit', admin: 'full', superadmin: 'full' },

  // RAG Document Management
  ragManagement: { viewer: 'hidden', editor: 'edit', admin: 'full', superadmin: 'full' },

  // Action Button Features
  generateSQL: { viewer: 'view_only', editor: 'view_only', admin: 'edit', superadmin: 'full' },
  runSQL: { viewer: 'hidden', editor: 'hidden', admin: 'edit', superadmin: 'full' },
  export: { viewer: 'view_only', editor: 'edit', admin: 'full', superadmin: 'full' },
  loadGuide: { viewer: 'hidden', editor: 'edit', admin: 'full', superadmin: 'full' },

  // Navigation Tabs
  aiTools: { viewer: 'hidden', editor: 'view_only', admin: 'edit', superadmin: 'full' },
  drafts: { viewer: 'hidden', editor: 'edit', admin: 'full', superadmin: 'full' },
  metadata: { viewer: 'view_only', editor: 'edit', admin: 'full', superadmin: 'full' },
  pois: { viewer: 'view_only', editor: 'edit', admin: 'full', superadmin: 'full' },
  preview: { viewer: 'view_only', editor: 'view_only', admin: 'view_only', superadmin: 'full' },
  map: { viewer: 'view_only', editor: 'view_only', admin: 'view_only', superadmin: 'full' },

  // Analytics Features
  analytics: { viewer: 'hidden', editor: 'view_only', admin: 'view_only', superadmin: 'full' },
  analyticsOverview: { viewer: 'hidden', editor: 'view_only', admin: 'view_only', superadmin: 'full' },
  analyticsDaily: { viewer: 'hidden', editor: 'view_only', admin: 'view_only', superadmin: 'full' },
  analyticsQR: { viewer: 'hidden', editor: 'view_only', admin: 'view_only', superadmin: 'full' },
  analyticsAudio: { viewer: 'hidden', editor: 'view_only', admin: 'view_only', superadmin: 'full' },
  analyticsGeo: { viewer: 'hidden', editor: 'view_only', admin: 'view_only', superadmin: 'full' },
  analyticsSessions: { viewer: 'hidden', editor: 'view_only', admin: 'view_only', superadmin: 'full' },

  // VPR (Visual Place Recognition)
  vpr: { viewer: 'hidden', editor: 'view_only', admin: 'edit', superadmin: 'full' }
};

export const AccessControlProvider = ({ children }) => {
  const { userRole } = useAuth();
  const [permissions, setPermissions] = useState(DEFAULT_PERMISSIONS);
  const [loading, setLoading] = useState(false);

  // Initialize permissions in Firebase if they don't exist
  const initializePermissions = async () => {
    try {
      const permissionsSnapshot = await getDocs(collection(db, 'permissions'));

      if (permissionsSnapshot.empty) {
        // No permissions exist, create them
        const batch = [];
        for (const [fieldName, fieldPermissions] of Object.entries(DEFAULT_PERMISSIONS)) {
          batch.push(setDoc(doc(db, 'permissions', fieldName), fieldPermissions));
        }
        await Promise.all(batch);
      }
    } catch (error) {
      console.error('Error initializing permissions:', error);
    }
  };

  // Load permissions from Firestore with real-time updates
  useEffect(() => {
    let unsubscribe;
    let canceled = false;

    const loadPermissionsRealTime = async () => {
      try {
        // Prime UI with defaults immediately; Firestore data is an async enhancement.
        setPermissions(DEFAULT_PERMISSIONS);
        setLoading(false);

        // Set up real-time listener
        unsubscribe = onSnapshot(collection(db, 'permissions'), (snapshot) => {
          if (canceled) {
            return;
          }

          const loadedPermissions = {};

          snapshot.forEach((doc) => {
            loadedPermissions[doc.id] = doc.data();
          });

          // If no permissions were loaded, use defaults
          if (Object.keys(loadedPermissions).length === 0) {
            setPermissions(DEFAULT_PERMISSIONS);
          } else {
            // Merge with defaults to ensure all fields are covered
            const mergedPermissions = { ...DEFAULT_PERMISSIONS };

            Object.keys(loadedPermissions).forEach(fieldName => {
              if (DEFAULT_PERMISSIONS[fieldName]) {
                mergedPermissions[fieldName] = {
                  ...DEFAULT_PERMISSIONS[fieldName],
                  ...loadedPermissions[fieldName]
                };
              }
            });

            // Ensure superadmin always has 'full' access
            Object.keys(mergedPermissions).forEach(field => {
              mergedPermissions[field].superadmin = 'full';
            });

            setPermissions(mergedPermissions);
          }
        }, (error) => {
          console.error('Firestore listener error:', error);
          setPermissions(DEFAULT_PERMISSIONS);
          setLoading(false);
        });

        // Initialize permissions in the background; do not block rendering on it.
        initializePermissions().catch((error) => {
          console.error('Error initializing permissions:', error);
        });
      } catch (error) {
        console.error('Error setting up permissions listener:', error);
        setPermissions(DEFAULT_PERMISSIONS);
        setLoading(false);
      }
    };

    loadPermissionsRealTime();

    // Cleanup function
    return () => {
      canceled = true;
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  // Check if user has access to a field
  const hasFieldAccess = (fieldName, requiredAccess = 'view_only') => {
    if (!userRole) {
      return false;
    }

    const fieldPerms = permissions[fieldName];
    if (!fieldPerms) {
      return false;
    }

    const userAccess = fieldPerms[userRole];
    if (!userAccess) {
      return false;
    }

    if (userAccess === 'hidden') {
      return false;
    }

    const accessLevels = ['hidden', 'view_only', 'edit', 'full'];
    const userLevel = accessLevels.indexOf(userAccess);
    const requiredLevel = accessLevels.indexOf(requiredAccess);

    const hasAccess = userLevel >= requiredLevel;

    return hasAccess;
  };

  // Get exact access level for a field
  const getFieldAccess = (fieldName) => {
    if (!userRole) return 'hidden';

    const fieldPerms = permissions[fieldName];
    if (!fieldPerms) return 'hidden';

    const access = fieldPerms[userRole] || 'hidden';
    return access;
  };

  // Update permissions (superadmin only)
  const updateFieldPermissions = async (fieldName, role, accessLevel) => {
    if (userRole !== 'superadmin') {
      return false;
    }

    try {
      // Get current field permissions
      const currentFieldPermissions = permissions[fieldName] || DEFAULT_PERMISSIONS[fieldName] || {};

      // Create updated field permissions
      const updatedFieldPermissions = {
        ...currentFieldPermissions,
        [role]: accessLevel
      };

      // Save to Firestore
      await setDoc(doc(db, 'permissions', fieldName), updatedFieldPermissions);

      return true;
    } catch (error) {
      console.error('Error updating permissions:', error);
      return false;
    }
  };

  // Check action permissions
  const canPerformAction = (action) => {
    if (!userRole) return false;
    
    const roleHierarchy = { viewer: 1, editor: 2, admin: 3, superadmin: 4 };
    const userLevel = roleHierarchy[userRole] || 0;
    
    switch (action) {
      case 'create_poi': return userLevel >= 2; // editor+
      case 'delete_poi': return userLevel >= 3; // admin+
      case 'manage_users': return userLevel >= 3; // admin+
      case 'configure_permissions': return userLevel >= 4; // superadmin only
      case 'generate_ai_content': return userLevel >= 2; // editor+
      case 'access_system_settings': return userLevel >= 3; // admin+
      default: return false;
    }
  };

  const value = {
    permissions,
    hasFieldAccess,
    getFieldAccess,
    updateFieldPermissions,
    canPerformAction,
    loading,
    FIELD_ACCESS_LEVELS
  };

  if (loading) {
    return <div>Loading permissions...</div>;
  }

  return (
    <AccessControlContext.Provider value={value}>
      {children}
    </AccessControlContext.Provider>
  );
};
